import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  FaUserGraduate,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaBook,
  FaUniversity,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import StudentService from "../../../services/studentService";
import AddStudentModal from "./AddStudentModal";
import AssignToBatchModal from "../../../components/admin/AssignToBatchModal";
import ExportButton from "../../../components/common/ExportButton";

const ManageStudent = () => {
  const navigate = useNavigate();

  // State
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search and Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc", // Latest first by default
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [studentBatches, setStudentBatches] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAndSortStudents();
  }, [students, searchTerm, sortConfig]);

  const loadData = async () => {
    try {
      setLoading(true);
      const studentsRes = await StudentService.getAllStudents();
      // Sort by created_at desc (latest first)
      const sortedStudents = (studentsRes.data || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
      setStudents(sortedStudents);
      setFilteredStudents(sortedStudents);
    } catch (error) {
      toast.error("Failed to load data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudentBatches = async (studentId) => {
    try {
      const response = await StudentService.getStudentBatches(studentId);
      setStudentBatches(response.data || []);
    } catch (error) {
      console.error("Failed to load student batches:", error);
      setStudentBatches([]);
    }
  };

  const filterAndSortStudents = () => {
    let filtered = [...students];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.phone.includes(searchTerm),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "created_at") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredStudents(filtered);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-muted" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="text-primary" />
    ) : (
      <FaSortDown className="text-primary" />
    );
  };

  const handleViewStudent = async (student) => {
    try {
      setLoadingDetails(true);
      setSelectedStudent(student);
      await loadStudentBatches(student.id);
      const response = await StudentService.getStudentDetails(student.id);
      setStudentDetails(response.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error("Failed to load student details");
      console.error(error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleEditStudent = (student) => {
    navigate(`/admin/students/edit/${student.id}`);
  };

  const handleDeleteStudent = (student) => {
    setSelectedStudent(student);
    setShowDeleteModal(true);
  };

  const handleAssignToBatch = (student) => {
    setSelectedStudent(student);
    setShowAssignModal(true);
  };

  const confirmDelete = async () => {
    try {
      await StudentService.deleteStudent(selectedStudent.id);
      toast.success("Student deleted successfully");
      setShowDeleteModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete student");
    }
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    loadData();
  };

  const handleAssignSuccess = () => {
    setShowAssignModal(false);
    loadData();
    toast.success("Student assigned successfully");
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge bg="success">Approved</Badge>;
      case "pending":
        return <Badge bg="warning">Pending</Badge>;
      case "rejected":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Get course count display
  const getCourseCountDisplay = (student) => {
    const count = student.total_courses || 0;
    if (count === 0) return <span className="text-muted">No courses</span>;
    if (count === 1) return <span>1 course</span>;
    return <span>{count} courses</span>;
  };

  const handleExportStudents = async () => {
    await StudentService.exportStudents({
      search: searchTerm,
      status: "all",
      sort_by: sortConfig.key,
      sort_order: sortConfig.direction,
    });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      {/* Fixed Header Section */}
      <div className="bg-white text-blue py-3 px-4 mb-3">
        <Row className="align-items-center">
          <Col>
            <h3 className="d-inline-block mb-0">
              <FaUserGraduate className="me-2" />
              Manage Students
            </h3>
          </Col>
          <Col className="text-end">
            <Button variant="light" onClick={() => setShowAddModal(true)}>
              <FaPlus className="me-2" /> Add New Student
            </Button>
            &nbsp; &nbsp;
            <ExportButton
              onExport={handleExportStudents}
              label="Export Students"
              variant="success"
              size="md"
            />
          </Col>
        </Row>
      </div>

      {/* Fixed Search Bar */}
      <div className="bg-white border-bottom py-3 px-4">
        <Row>
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by name, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>

      {/* Scrollable Table Section */}
      <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
        <Card className="shadow-sm">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped hover responsive className="mb-0">
                <thead
                  className="bg-light"
                  style={{ position: "sticky", top: 0, zIndex: 1 }}
                >
                  <tr>
                    <th style={{ width: "50px" }}>S.L</th>
                    <th
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer" }}
                    >
                      Student Name {getSortIcon("name")}
                    </th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Enrolled Course</th>
                    <th
                      onClick={() => handleSort("created_at")}
                      style={{ cursor: "pointer" }}
                    >
                      Registered On {getSortIcon("created_at")}
                    </th>
                    <th style={{ width: "140px" }}>Assign to Batch</th>
                    <th>Status</th>
                    <th style={{ width: "140px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <h5 className="text-muted">No students found</h5>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((student, index) => (
                      <tr key={student.id}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>
                          <strong>{student.name}</strong>
                        </td>
                        <td>{student.email}</td>
                        <td>{student.phone}</td>
                        <td>{getCourseCountDisplay(student)}</td>
                        <td>{formatDate(student.created_at)}</td>
                        <td>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleAssignToBatch(student)}
                            title="Assign to Batch"
                            className="w-100"
                          >
                            <FaUniversity className="me-1" />
                            Assign to Batch
                          </Button>
                        </td>
                        <td>{getStatusBadge(student.status)}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-1"
                            onClick={() => handleViewStudent(student)}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEditStudent(student)}
                            title="Edit Student"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteStudent(student)}
                            title="Delete Student"
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Pagination */}
        {filteredStudents.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, filteredStudents.length)} of{" "}
              {filteredStudents.length} entries
            </div>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                className="me-2"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="mx-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                className="ms-2"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* View Student Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserGraduate className="me-2 text-primary" />
            Student Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            studentDetails && (
              <>
                <Row className="mb-4">
                  <Col md={6}>
                    <h5>{studentDetails.student?.name}</h5>
                    <p className="mb-1">
                      <strong>Email:</strong> {studentDetails.student?.email}
                    </p>
                    <p className="mb-1">
                      <strong>Phone:</strong> {studentDetails.student?.phone}
                    </p>
                    <p className="mb-1">
                      <strong>Joined:</strong>{" "}
                      {formatDate(studentDetails.student?.created_at)}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Total Courses:</strong>{" "}
                      {studentDetails.summary?.total_courses || 0}
                    </p>
                    <p>
                      <strong>Total Fees:</strong> ₹
                      {studentDetails.summary?.total_fees?.toLocaleString() ||
                        0}
                    </p>
                    <p>
                      <strong>Total Paid:</strong> ₹
                      {studentDetails.summary?.total_paid?.toLocaleString() ||
                        0}
                    </p>
                    <p>
                      <strong>Total Due:</strong> ₹
                      {studentDetails.summary?.total_due?.toLocaleString() || 0}
                    </p>
                  </Col>
                </Row>

                <h6 className="mb-3">Enrolled Courses</h6>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Duration</th>
                      <th>Total Fees</th>
                      <th>Paid</th>
                      <th>Due</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentDetails.courses?.map((course) => (
                      <tr key={course.course_id}>
                        <td>{course.course_name}</td>
                        <td>{course.duration}</td>
                        <td>₹{course.total_fees?.toLocaleString()}</td>
                        <td className="text-success">
                          ₹{course.paid_amount?.toLocaleString()}
                        </td>
                        <td
                          className={course.due_amount > 0 ? "text-danger" : ""}
                        >
                          ₹{course.due_amount?.toLocaleString()}
                        </td>
                        <td>
                          {course.due_amount === 0 ? (
                            <Badge bg="success">Paid</Badge>
                          ) : (
                            <Badge bg="warning">Due</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Assigned Batches Section */}
                {studentBatches.length > 0 && (
                  <>
                    <h6 className="mb-3 mt-4">Assigned Batches</h6>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Batch Name</th>
                          <th>Course</th>
                          <th>Start Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentBatches.map((batch, idx) => (
                          <tr key={idx}>
                            <td>
                              <strong>{batch.batch_name}</strong>
                            </td>
                            <td>{batch.course_name}</td>
                            <td>{formatDate(batch.start_date)}</td>
                            <td>
                              <Badge
                                bg={
                                  batch.batch_status === "active"
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {batch.batch_status || "Active"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <>
              <p>Are you sure you want to delete student:</p>
              <p className="fw-bold text-danger">{selectedStudent.name}</p>
              <Alert variant="warning">
                This action cannot be undone. All student data including
                enrollments and transactions will be permanently deleted.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Student Modal */}
      <AddStudentModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Assign to Batch Modal */}
      <AssignToBatchModal
        show={showAssignModal}
        onHide={() => {
          setShowAssignModal(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
        onSuccess={handleAssignSuccess}
      />
    </div>
  );
};

export default ManageStudent;
